import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../api/endpoints.js';
import { Page } from '../../components/AppShell.jsx';
import {
  Badge, Button, Card, DataTable, DefinitionList, Dialog, ErrorState, Field, Loading, Metric, Textarea,
} from '../../components/ui.jsx';
import { toast } from '../../lib/toast.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { PERMISSIONS } from '../../lib/permissions.js';
import { useCreditAdjustment } from '../credits/adjust.jsx';
import { date, dateNumeric, humanise, number, phoneWithCountryCode, relative } from '../../lib/format.js';
import { DocumentPreviews } from '../../components/DocumentPreviews.jsx';
import { DealsCard, EnquiriesCard, LeadsCard, RequirementsCard } from '../records/RecordCards.jsx';
import { useAccountVisibility } from './visibility.jsx';

/**
 * One account, in full.
 *
 * The list answers "who is on the platform"; this answers "what is going on
 * with this person" — what they have spent, whether they finished setting up,
 * and whether anyone has had to act on them before.
 */
export default function UserDetailPage() {
  const { id } = useParams();
  const { can } = useAuth();

  const canSuspend = can(PERMISSIONS.USER_SUSPEND);
  // Retiring an account is a heavier act than freezing one, so it is a
  // separate permission rather than something every moderator inherits.
  const canDelete = can(PERMISSIONS.USER_MANAGE);
  const canAdjustCredits = can(PERMISSIONS.CREDIT_ADJUST);

  const credits = useCreditAdjustment();
  const visibility = useAccountVisibility();
  const canSeeLedger = can(PERMISSIONS.FINANCE_VIEW);
  const canReviewIdentity = can(PERMISSIONS.MODERATION_ACT);
  // Requirements and enquiries are read from the lead lists, under their permission.
  const canSeeRecords = can(PERMISSIONS.LEAD_VIEW);
  const [identityDecision, setIdentityDecision] = useState(null);

  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['admin-user', id],
    queryFn: () => api.users.detail(id),
  });

  const ledger = useQuery({
    queryKey: ['admin-user-ledger', id],
    queryFn: () => api.credits.ledger({ user: id, pageSize: 10 }),
    enabled: Boolean(canSeeLedger),
  });

  const navigate = useNavigate();

  const [pending, setPending] = useState(null);
  const [reason, setReason] = useState('');
  const [isWorking, setIsWorking] = useState(false);

  const user = query.data;

  const close = () => {
    setPending(null);
    setReason('');
  };

  const act = async () => {
    setIsWorking(true);

    try {
      if (pending === 'suspend') {
        await api.users.suspend(id, reason.trim());
        toast.success(`${user.name || 'That account'} has been suspended.`);
      } else if (pending === 'delete') {
        await api.users.remove(id, reason.trim());
        toast.success(`${user.name || 'That account'} has been deleted.`);
        close();
        queryClient.invalidateQueries({ queryKey: ['admin-users'] });
        // The record it was showing is gone from every list it appeared in.
        navigate('/users');
        return;
      } else {
        await api.users.reinstate(id);
        toast.success(`${user.name || 'That account'} is active again.`);
      }

      close();
      queryClient.invalidateQueries({ queryKey: ['admin-user', id] });
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsWorking(false);
    }
  };

  if (query.isLoading) {
    return (
      <Page title="Account">
        <Loading label="Loading account" />
      </Page>
    );
  }

  if (query.isError) {
    return (
      <Page title="Account" actions={<BackToUsers />}>
        <ErrorState error={query.error} onRetry={query.refetch} title="Could not load this account" />
      </Page>
    );
  }

  const isSuspended = user.status === 'suspended';
  const isHidden = Boolean(user.hiddenFromPublic);
  // Reinstating undoes a suspension; it is the one action here without a reason.
  const needsReason = pending !== 'reinstate';

  const ledgerColumns = [
    {
      key: 'when',
      header: 'When',
      render: (row) => <span className="table__muted nowrap">{dateNumeric(row.createdAt)}</span>,
    },
    {
      key: 'reason',
      header: 'Reason',
      render: (row) => (
        <div>
          <div>{humanise(row.type)}</div>
          <div className="table__muted truncate">{row.description}</div>
        </div>
      ),
    },
    {
      key: 'amount',
      header: 'Credits',
      className: 'table__numeric',
      render: (row) => (
        <span className={`nowrap ${row.amount > 0 ? 'credits--in' : 'credits--out'}`}>
          {row.amount > 0 ? '+' : '−'}
          {number(Math.abs(row.amount))}
        </span>
      ),
    },
    {
      key: 'balance',
      header: 'Balance after',
      className: 'table__numeric',
      render: (row) => <span className="table__muted">{number(row.balanceAfter)}</span>,
    },
  ];

  return (
    <Page
      title={user.name || 'No name yet'}
      subtitle={phoneWithCountryCode(user.phone)}
      actions={
        <>
          <BackToUsers kind={user.accountKind} />
          {canSuspend ? (
            <Button
              variant={isSuspended ? 'primary' : 'danger'}
              onClick={() => setPending(isSuspended ? 'reinstate' : 'suspend')}
            >
              {isSuspended ? 'Reinstate' : 'Suspend'}
            </Button>
          ) : null}
          {canSuspend && !user.deletedAt ? (
            <Button
              variant="outline"
              onClick={() => visibility.ask({ userId: id, name: user.name, hidden: isHidden })}
            >
              {isHidden ? 'Show to real users' : 'Hide from real users'}
            </Button>
          ) : null}
          {canAdjustCredits ? (
            <Button
              variant="outline"
              onClick={() => credits.ask({ userId: id, name: user.name || 'this account' })}
            >
              Adjust credits
            </Button>
          ) : null}
          {canDelete && !user.deletedAt ? (
            <Button variant="ghost" onClick={() => setPending('delete')}>
              Delete
            </Button>
          ) : null}
        </>
      }
    >
      <div className="stack">
        <div className="row" style={{ flexWrap: 'wrap' }}>
          <Badge status={user.status} />
          {user.profileCompletedAt ? null : <Badge tone="warning" label="Setup not finished" />}
          {user.isTestAccount ? <Badge tone="neutral" label="Test account" /> : null}
          {isHidden ? <Badge tone="warning" label="Hidden from real users" /> : null}
          {user.broker ? (
            <Link className="table__link" to={`/brokers/${user.broker._id}`}>
              <Badge tone="info" label={`Agency · ${user.broker.businessName}`} />
            </Link>
          ) : null}
        </div>

        <div className="metric-grid">
          <Metric
            label="Credit balance"
            value={number(user.creditBalance || 0)}
            caption="One wallet, on this account"
          />
          <Metric
            label="Listings"
            value={number(user.stats?.listings || 0)}
            caption={`${number(user.stats?.liveListings || 0)} live`}
          />
          <Metric label="Requirements posted" value={number(user.stats?.requirements || 0)} />
          <Metric
            label="Last seen"
            value={user.lastLoginAt ? relative(user.lastLoginAt) : 'Never'}
            caption={user.lastLoginAt ? dateNumeric(user.lastLoginAt) : undefined}
          />
        </div>

        <div className="two-column">
          <div className="stack">
            {isSuspended ? (
              <Card title="Suspended" subtitle="Their account is closed to them until this is lifted.">
                <DefinitionList
                  items={[
                    {
                      label: 'Since',
                      value: user.suspension?.suspendedAt ? date(user.suspension.suspendedAt) : null,
                    },
                    { label: 'Until', value: user.suspension?.until ? date(user.suspension.until) : 'No end date' },
                    { label: 'Reason', value: user.suspension?.reason },
                  ]}
                />
              </Card>
            ) : null}

            {canSeeLedger ? (
              <Card
                title="Credits"
                subtitle="What this account has spent and topped up, newest first."
                bodyless
              >
                <DataTable
                  columns={ledgerColumns}
                  rows={ledger.data?.items || []}
                  query={ledger}
                  empty={{
                    title: 'Nothing spent yet',
                    body: 'This account has not put an ad up or unlocked a contact.',
                  }}
                />
              </Card>
            ) : null}

            {canSeeRecords ? (
              <>
                <RequirementsCard userId={id} />
                <LeadsCard userId={id} />
                <EnquiriesCard userId={id} />
                {can(PERMISSIONS.DEAL_VIEW) ? <DealsCard userId={id} /> : null}
              </>
            ) : null}

            <Card
              title="Identity"
              subtitle="Optional for tenants and owners — sent from Profile → Verify yourself in the app."
              actions={
                canReviewIdentity && user.identityVerification?.status === 'pending' ? (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => setIdentityDecision('reject')}>
                      Reject
                    </Button>
                    <Button size="sm" variant="primary" onClick={() => setIdentityDecision('approve')}>
                      Approve
                    </Button>
                  </>
                ) : null
              }
            >
              <DefinitionList
                items={[
                  { label: 'Status', value: humanise(user.identityVerification?.status) },
                  {
                    label: 'Submitted',
                    value: user.identityVerification?.submittedAt
                      ? date(user.identityVerification.submittedAt)
                      : null,
                  },
                  {
                    label: 'Reviewed',
                    value: user.identityVerification?.reviewedAt
                      ? date(user.identityVerification.reviewedAt)
                      : null,
                  },
                  {
                    label: 'Documents',
                    value: user.identityVerification?.documents?.length ? (
                      <DocumentPreviews documents={user.identityVerification.documents} />
                    ) : null,
                  },
                  { label: 'Rejection reason', value: user.identityVerification?.rejectionReason },
                ]}
              />
              {user.identityVerification?.documents?.some((doc) => doc.url) ? (
                <p className="muted" style={{ marginTop: 'var(--space-3)', fontSize: 12 }}>
                  Links are private and expire shortly — reload the page for fresh ones.
                </p>
              ) : null}
            </Card>
          </div>

          <div className="stack">
            <Card title="Account">
              <DefinitionList
                items={[
                  { label: 'Phone', value: phoneWithCountryCode(user.phone) },
                  {
                    label: 'Other number',
                    value: user.alternatePhone ? phoneWithCountryCode(user.alternatePhone) : null,
                  },
                  { label: 'Phone verified', value: user.isPhoneVerified ? 'Yes' : 'No' },
                  { label: 'Joined', value: dateNumeric(user.createdAt) },
                  {
                    label: 'Finished setup',
                    value: user.profileCompletedAt ? dateNumeric(user.profileCompletedAt) : 'Not yet',
                  },
                ]}
              />
            </Card>

            {user.address?.city ? (
              <Card title="Where they live" subtitle="Not where any of their listings are.">
                <DefinitionList
                  items={[
                    { label: 'Locality', value: user.address.locality },
                    { label: 'City', value: user.address.city },
                    { label: 'State', value: user.address.state },
                  ]}
                />
              </Card>
            ) : null}

          </div>
        </div>
      </div>

      {credits.dialog}
      {visibility.dialog}

      <Dialog
        open={Boolean(pending)}
        onClose={close}
        title={
          {
            delete: `Delete ${user.name || 'this account'}?`,
            suspend: `Suspend ${user.name || 'this account'}?`,
            reinstate: `Reinstate ${user.name || 'this account'}?`,
          }[pending]
        }
        warning={
          {
            delete:
              'They are signed out everywhere, cannot sign back in, and drop off every list here. Their listings come down and their open requirements are cancelled; their wishlist, saved listings, tenant profile and notifications are removed. A broker account retires its agency and the agency’s whole inventory with it. Leads, enquiries, payments and messages stay, because other people’s records point at them. This is not reversible from the panel, and it is recorded in the audit log against your account.',
            suspend:
              'They are signed out everywhere and cannot sign back in. Their listings, requirements and messages are left as they are. This is reversible, and it is recorded in the audit log against your account.',
            reinstate:
              'They can sign in again from now. The suspension stays in the audit log.',
          }[pending]
        }
        footer={
          <>
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button
              variant={pending === 'reinstate' ? 'primary' : 'danger'}
              onClick={act}
              loading={isWorking}
              disabled={needsReason && reason.trim().length < 3}
            >
              {
                {
                  delete: 'Delete account',
                  suspend: 'Suspend account',
                  reinstate: 'Reinstate account',
                }[pending]
              }
            </Button>
          </>
        }
      >
        {needsReason ? (
          <Field label="Reason" hint="Recorded in the audit log against your account.">
            <Textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={
                {
                  delete: 'Why is this account being deleted?',
                  suspend: 'Why is this account being suspended?',
                }[pending]
              }
            />
          </Field>
        ) : (
          <p className="muted" style={{ fontSize: 14 }}>
            {user.suspension?.reason
              ? `They were suspended for: ${user.suspension.reason}`
              : 'No reason was recorded for the suspension.'}
          </p>
        )}
      </Dialog>

      <IdentityDecisionDialog
        userId={id}
        name={user.name}
        decision={identityDecision}
        onClose={() => setIdentityDecision(null)}
      />
    </Page>
  );
}

/**
 * Approve, or reject with a reason. The person is notified either way, and a
 * rejection shows them the reason in the app so they can send better documents.
 */
function IdentityDecisionDialog({ userId, name, decision, onClose }) {
  const queryClient = useQueryClient();
  const [why, setWhy] = useState('');

  const close = () => {
    setWhy('');
    onClose();
  };

  const decide = useMutation({
    mutationFn: () =>
      api.users.decideIdentity(userId, decision === 'reject' ? { decision, reason: why.trim() } : { decision }),
    onSuccess: () => {
      toast.success(decision === 'approve' ? `${name || 'This person'} is verified.` : 'Documents rejected. They have been told why.');
      queryClient.invalidateQueries({ queryKey: ['admin-user', userId] });
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      close();
    },
    onError: (error) => toast.error(error.message || 'That decision could not be recorded.'),
  });

  const rejecting = decision === 'reject';

  return (
    <Dialog
      open={Boolean(decision)}
      onClose={close}
      title={rejecting ? 'Reject these documents' : 'Approve these documents'}
      warning={
        rejecting
          ? 'They are told the documents were not accepted, with the reason you give, and can send new ones.'
          : 'Their profile shows them as verified, and they are told. Check the name and photo match the account first.'
      }
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            variant={rejecting ? 'danger' : 'primary'}
            loading={decide.isPending}
            disabled={rejecting && why.trim().length < 3}
            onClick={() => decide.mutate()}
          >
            {rejecting ? 'Reject' : 'Approve'}
          </Button>
        </>
      }
    >
      {rejecting ? (
        <Field label="Reason" hint="Required. Shown to them in the app — say what to fix.">
          <Textarea rows={3} value={why} onChange={(event) => setWhy(event.target.value)} placeholder="The photo is blurred; send a clearer scan of the front" />
        </Field>
      ) : null}
    </Dialog>
  );
}

/**
 * Back to the list the account came from. A broker account only lands here
 * when it has no agency yet — every other broker is managed on its agency's
 * page — so that list is the Brokers tab of accounts still onboarding.
 */
function BackToUsers({ kind }) {
  return kind === 'broker' ? (
    <Link to="/brokers?tab=unfinished">
      <Button variant="ghost">All brokers</Button>
    </Link>
  ) : (
    <Link to="/users">
      <Button variant="ghost">All users</Button>
    </Link>
  );
}
